import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CircleSlash, PlusCircle, FilterIcon, ArrowLeft, AlertTriangle, Edit2, Check, X, Ban } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import { useToast } from '@/components/ui/use-toast';
import { washingCyclesColumns, columnsExcel } from './utils/washingCyclesColumns';
import { fetchWashingCycles, fetchAbortedWashingCycles, createWashingCycle, updateWashingCycle, deleteWashingCycle, exportWashingCycles } from './services/washingCycles.services';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';
import AppliedFilters from '@/components/AppliedFilters';
import DynamicFormModal from '@/components/DynamicFormModal';
import EditWashingCycleModal from './components/EditWashingCycleModal';
import EditAbortedWashingCycleModal from './components/EditAbortedWashingCycleModal';
import AbortedCyclesModal from '@/pages/reception/components/AbortedCyclesModal';
import { useAuth } from '@/contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { fetchMachines, updateMachineCounter } from '@/pages/parametrizacion/machines/Services/machines.services';

const ITEMS_PER_PAGE = 10;

/**
 * Columnas dedicadas para la TABLA de ciclos abortados (no para el
 * modal). Es un subset enfocado: como acá TODAS las filas son
 * abortados, no mostramos la columna "Estado" y resaltamos la "Razón"
 * (que es lo que el operador suele necesitar consultar después).
 * El re-render usa el mismo formato de fecha que la tabla principal
 * para que el operador no tenga que traducir mentalmente entre
 * vistas.
 */
const abortedColumns = [
  {
    id: 'fecha',
    label: 'Fecha',
    sortable: true,
    render: (row) => {
      try {
        if (!row.fecha) return '-';
        const date = new Date(row.fecha);
        if (Number.isNaN(date.getTime())) return 'Fecha inválida';
        return date.toLocaleDateString('es-CO', { year: 'numeric', month: '2-digit', day: '2-digit' });
      } catch (error) {
        return '-';
      }
    },
  },
  {
    id: 'maquina',
    label: 'Lavadora',
    sortable: true,
    render: (row) => row?.maquina?.nombre || '-',
  },
  {
    id: 'ciclos',
    label: 'Ciclos',
    sortable: true,
    render: (row) => (row.ciclos ?? '-'),
  },
  {
    id: 'razonAbortado',
    label: 'Razón',
    sortable: false,
    render: (row) => {
      const razon = row?.razonAbortado;
      if (!razon) return <span className="text-muted-foreground text-xs">Sin razón</span>;
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
          {razon}
        </span>
      );
    },
  },
  {
    id: 'capacidad',
    label: 'Capacidad',
    sortable: false,
    render: (row) => (row.capacidad != null ? `${row.capacidad} kg` : '-'),
  },
  {
    id: 'contador',
    label: 'Contador',
    sortable: true,
    render: (row) => (row.contador ?? '-'),
  },
  {
    // Columna de opciones: "Editar" y "Eliminar" (este último sólo
    // para administradores, validado en `handleDeleteConfirmation`). El botón se
    // renderiza automáticamente por el `DataTable` cuando el id
    // de la columna es "opciones" o "actions" — basta con pasar
    // el `onAction` correspondiente a nivel de <DataTable />.
    id: 'opciones',
    label: 'Opciones',
    sortable: false,
    actions: [
      {
        key: 'editAborted',
        icon: 'Edit',
        tooltip: 'Editar ciclo abortado',
        className: 'text-amber-600 hover:text-amber-800',
      },
      {
        key: 'delete',
        icon: 'Trash',
        tooltip: 'Eliminar ciclo abortado (solo administradores)',
        className: 'text-red-400 hover:text-red-600',
      },
    ],
  },
];

const WashingCyclesPage = () => {
  const { idLavanderia } = useParams();
  const { toast } = useToast();
  const { isAdmin } = useAuth(); // Obtener función para verificar si es administrador
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [laundryName, setLaundryName] = useState('');

  // Obtener la fecha de hoy en formato ISO para el filtro
  const today = new Date();
  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1, 0, 0, 0).toISOString();
  const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59).toISOString();

  // Inicializar filtros con la fecha actual y el ID de lavandería
  const [filters, setFilters] = useState({
    lavanderia: {
      id: idLavanderia,
    },
    fecha: {
      $gte: startOfDay,
      $lte: endOfDay
    }
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [machines, setMachines] = useState([]);
  const [loadingMachines, setLoadingMachines] = useState(true);
  const [columns, setColumns] = useState([]);
  const [excelColumns, setExcelColumns] = useState(columnsExcel);
  const [machineCounters, setMachineCounters] = useState({});
  const [newCounterValues, setNewCounterValues] = useState({});
  const [editingCounter, setEditingCounter] = useState({});
  const [tempCounterValues, setTempCounterValues] = useState({});
  const [selectedDates, setSelectedDates] = useState({});
  // Estado del modal de ciclos abortados. La lógica interna del modal
  // (formulario, guardado, lista paginada de recientes) vive dentro
  // del componente <AbortedCyclesModal />.
  const [abortedModalOpen, setAbortedModalOpen] = useState(false);

  // Estado de la TABLA de ciclos abortados en esta página.
  // Es independiente del modal: muestra el histórico completo de
  // ciclos abortados del centro, con su propio paginador, para que
  // el operador los vea siempre visibles y no se confundan con los
  // ciclos normales.
  // NOTA: NO se incluye `abortado: true` en los filtros porque el
  // endpoint `/washing-cycles/aborted/filter` lo fuerza internamente.
  const [abortedData, setAbortedData] = useState([]);
  const [abortedTotalItems, setAbortedTotalItems] = useState(0);
  const [abortedCurrentPage, setAbortedCurrentPage] = useState(1);
  const [abortedLoading, setAbortedLoading] = useState(false);
  const [abortedFilters, setAbortedFilters] = useState({
    lavanderia: { id: idLavanderia },
  });

  // Estado para edición de un ciclo abortado desde la tabla inferior.
  // `abortedItemToEdit` recibe el row completo; el modal nuevo se
  // encarga de prellenar el formulario con esos datos.
  const [abortedItemToEdit, setAbortedItemToEdit] = useState(null);
  const [abortedEditModalOpen, setAbortedEditModalOpen] = useState(false);
  const [abortedEditSaving, setAbortedEditSaving] = useState(false);

  const buildQuery = (filters) => {
    const query = { ...filters };
    if (query.maquina && typeof query.maquina === 'string') {
      query['maquina.id'] = query.maquina;
      delete query.maquina;
    }
    return query;
  };

  const fetchAndSetWashingCycles = useCallback(async () => {
    setIsLoading(true);
    try {
      // IMPORTANTE: excluimos los ciclos abortados (`abortado: true`)
      // de esta tabla. Los abortados tienen su propia tabla dedicada
      // más abajo para evitar que se mezclen con los ciclos normales
      // y generen confusión. Usamos `$ne: true` en lugar de `false`
      // para incluir también registros viejos donde el campo
      // `abortado` aún no existía (todos aquellos sin el flag son
      // implícitamente "no abortados").
      const query = buildQuery({
        ...filters,
        abortado: { $ne: true },
      });
      const response = await fetchWashingCycles({
        limit: ITEMS_PER_PAGE,
        page: currentPage,
        query: query
      });

      const data = response?.data || [];
      const totalRecords = response?.totalRecords || 0;

      setDisplayedData(Array.isArray(data) ? data : []);
      setTotalItems(totalRecords);
    } catch (error) {
      setDisplayedData([]); // En caso de error, asegurar que siga siendo un array
      toast({
        title: 'Error',
        description: error.message || 'No se pudieron obtener los ciclos de lavado.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, filters, toast]);

  /**
   * Carga los ciclos ABORTADOS de la lavandería actual para alimentar
   * la tabla inferior de "Ciclos Abortados". Mantiene su propio estado
   * de paginación independiente del de la tabla principal.
   *
   * Usa el endpoint dedicado `fetchAbortedWashingCycles` (que
   * internamente fuerza `abortado: true` en el backend), de modo que
   * esta llamada no pueda mezclarse accidentalmente con la tabla
   * principal al cambiar de página.
   */
  const fetchAndSetAbortedCycles = useCallback(async () => {
    setAbortedLoading(true);
    try {
      const query = buildQuery(abortedFilters);
      const response = await fetchAbortedWashingCycles({
        limit: ITEMS_PER_PAGE,
        page: abortedCurrentPage,
        query,
      });

      const data = response?.data || [];
      const totalRecords = response?.totalRecords || 0;

      setAbortedData(Array.isArray(data) ? data : []);
      setAbortedTotalItems(totalRecords);
    } catch (error) {
      setAbortedData([]);
      // No spameamos al usuario con toasts: si falla, simplemente la
      // tabla queda vacía y puede volver a intentar con "Refrescar".
      console.error('Error cargando ciclos abortados:', error);
    } finally {
      setAbortedLoading(false);
    }
  }, [abortedCurrentPage, abortedFilters, toast]);

  // Cargar datos iniciales y configurar columnas
  useEffect(() => {
    const initialize = async () => {
      setLoadingMachines(true);
      try {
        const response = await fetchMachines({
          limit: 100,
          offset: 1,
          query: { "lavanderia.id": idLavanderia }
        });

        if (response && response.data) {
          const fetchedMachines = response.data;
          setMachines(fetchedMachines);

          // Inicializar contadores
          const countersData = {};
          const newCounters = {};
          fetchedMachines.forEach(machine => {
            countersData[machine._id] = machine.contador || 0;
            newCounters[machine._id] = machine.contador || 0;
          });

          if (fetchedMachines.length > 0) {
            setLaundryName(fetchedMachines[0].lavanderia.nombre);
          }

          setMachineCounters(countersData);
          setNewCounterValues(newCounters);

          // Configurar las columnas con los datos de las máquinas
          const machineOptions = fetchedMachines.map(machine => ({
            label: machine.nombre,
            value: machine._id
          }));

          const updatedColumns = [...washingCyclesColumns].map(column => {
            if (column.id === 'maquina') {
              return {
                ...column,
                options: machineOptions,
                onChange: (selected, formData) => {
                  if (selected && formData) {
                    const capacidad = selected.value.capacidad || 0;
                    const ciclos = formData.ciclos || 0;
                    return {
                      ...formData,
                      capacidad: capacidad,
                      kilosLavados: capacidad * ciclos
                    };
                  }
                  return formData;
                }
              };
            }

            if (column.id === 'ciclos') {
              return {
                ...column,
                onChange: (value, formData) => {
                  if (formData && formData.capacidad) {
                    const ciclos = value || 0;
                    const capacidad = formData.capacidad || 0;
                    return {
                      ...formData,
                      kilosLavados: capacidad * ciclos
                    };
                  }
                  return formData;
                }
              };
            }

            return column;
          });

          setColumns(updatedColumns);
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message || 'No se pudieron cargar las máquinas.',
          variant: 'destructive'
        });
      } finally {
        setLoadingMachines(false);
      }
    };

    initialize();
  }, [idLavanderia, toast]);

  // Efecto para cargar los ciclos de lavado cuando cambian los filtros o la página
  useEffect(() => {
    fetchAndSetWashingCycles();
  }, [fetchAndSetWashingCycles]);

  // Efecto para cargar la tabla de ciclos abortados cuando cambian
  // sus filtros o la página. Se monta junto con la página y se
  // refresca también desde el callback `onCycleCreated` del modal.
  useEffect(() => {
    fetchAndSetAbortedCycles();
  }, [fetchAndSetAbortedCycles]);

  const handleSaveItem = async (itemData) => {
    try {
      setIsLoading(true);
      
      // Normalizar máquina a objeto { id, nombre }
      let maquinaObj = null;
      const mv = itemData.maquina;
      if (mv) {
        if (typeof mv === 'object') {
          const id = mv.id || mv._id;
          const nombre = mv.nombre || machines.find(m => m._id === id || m.id === id)?.nombre || '';
          maquinaObj = id ? { id, nombre } : null;
        } else {
          // mv es un string (id)
          const mach = machines.find(m => m._id === mv || m.id === mv);
          maquinaObj = { id: mv, nombre: mach?.nombre || '' };
        }
      }

      // Validar contra el límite de consumo diario si está configurado
      if (maquinaObj) {
        const machine = machines.find(m => m._id === maquinaObj.id || m.id === maquinaObj.id);
        const ciclos = itemData.ciclos || 0;
        
        if (machine && machine.consumoLimiteDia && ciclos > machine.consumoLimiteDia) {
          toast({
            title: "Error - Consumo excesivo",
            description: `Los ciclos ingresados (${ciclos}) superan el límite diario configurado (${machine.consumoLimiteDia}) para ${machine.nombre}. Por favor verifique el contador ingresado.`,
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
      }

      // Obtener la fecha seleccionada para esta máquina o usar el día anterior por defecto
      const machineId = maquinaObj?.id;
      const dateToUse = (machineId && selectedDates[machineId]) || itemData.fecha || getDefaultDate();
      
      // Crear la fecha sin conversión de zona horaria
      // Usar UTC para evitar problemas de zona horaria
      const [year, month, day] = dateToUse.split('-');
      const recordDate = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0));

      // Crear objeto con formato adecuado para la API
      const formattedData = {
        ...itemData,
        maquina: maquinaObj || null,
        fecha: recordDate.toISOString(),
        lavanderia: itemData.lavanderia || { id: idLavanderia, nombre: laundryName }
      };

      // Determinar si es creación desde tarjeta o desde modal
      const isCardSave = !modalMode || modalMode === '' || modalMode === 'create' && !currentItem;
      
      if (isCardSave) {
        // Crear nuevo ciclo
        await createWashingCycle(formattedData);
        toast({ title: 'Éxito', description: 'Ciclos registrados correctamente' });

        // Si el guardado proviene de una tarjeta de máquina, actualizar el contador local
        if (itemData.maquina && itemData.maquina.id && newCounterValues[itemData.maquina.id]) {
          // Actualizar el contador en el estado
          setMachineCounters(prev => ({
            ...prev,
            [itemData.maquina.id]: newCounterValues[itemData.maquina.id]
          }));

          // También actualizar las máquinas para reflejar el nuevo contador
          setMachines(prev => prev.map(machine => {
            if (machine._id === itemData.maquina.id) {
              return {
                ...machine,
                contador: newCounterValues[itemData.maquina.id]
              };
            }
            return machine;
          }));
        }
      } else if (modalMode === 'create') {
        // Crear desde modal
        await createWashingCycle(formattedData);
        toast({ title: 'Éxito', description: 'Ciclos registrados correctamente' });
      } else {
        // Para actualización desde el modal personalizado, los datos ya vienen procesados
        await updateWashingCycle(currentItem._id, formattedData);
        toast({ title: 'Éxito', description: 'Ciclos actualizados correctamente' });
      }

      // Cerrar modal y refrescar datos
      setIsModalOpen(false);
      setCurrentItem(null);
      setModalMode('');  // Reset modal mode
      fetchAndSetWashingCycles();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Error al guardar los datos.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleOpenModal = (mode, item = null) => {
    // Verificar si el usuario es administrador para modo edición
    if (mode === 'edit' && !isAdmin()) {
      toast({
        title: "Acceso restringido",
        description: "Solo los administradores pueden editar registros.",
        variant: "destructive",
      });
      return;
    }
    
    setModalMode(mode);
    setCurrentItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentItem(null);
  };

  const handleDeleteConfirmation = (item) => {
    // Verificar si el usuario es administrador para eliminar
    if (!isAdmin()) {
      toast({
        title: "Acceso restringido",
        description: "Solo los administradores pueden eliminar registros.",
        variant: "destructive",
      });
      return;
    }
    
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteItem = async () => {
    try {
      if (itemToDelete) {
        const response = await deleteWashingCycle(itemToDelete._id);
        if (response.code === 200) {
          toast({ title: "Ciclo eliminado", description: "El registro fue eliminado correctamente." });
          fetchAndSetWashingCycles();
          fetchAndSetAbortedCycles();
        } else {
          toast({
            title: "Error",
            description: "No se pudo eliminar el registro.",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error("Error eliminando ciclo:", error);
      toast({
        title: "Error",
        description: error.message || "Ocurrió un problema al eliminar el registro.",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleAction = (actionType, row) => {
    switch (actionType) {
      case 'edit':
        handleOpenModal('edit', row);
        break;
      case 'delete':
        handleDeleteConfirmation(row);
        break;
      case 'editAborted':
        // Abre el modal de edición específico para ciclos abortados.
        // Reutilizamos el mismo `handleAction` para ambas tablas; las
        // action keys las diferencian.
        setAbortedItemToEdit(row);
        setAbortedEditModalOpen(true);
        break;
      default:
        break;
    }
  };

  /**
   * Cierra el modal de edición de abortados y limpia el item en
   * edición. Se hace en un handler dedicado para no acoplar la lógica
   * de cierre al `onClose` del modal (que también se invoca al
   * cancelar o al terminar el guardado).
   */
  const handleCloseAbortedEdit = () => {
    setAbortedEditModalOpen(false);
    setAbortedItemToEdit(null);
  };

  /**
   * Persiste la edición de un ciclo abortado.
   * Usa el endpoint general `updateWashingCycle` (que NO aplica el
   * "-1 día" sobre la fecha, a diferencia del create legacy), por
   * lo que es seguro reenviar la `fecha` que el operador acaba de
   * confirmar en el modal.
   */
  const handleSaveAbortedEdit = async (data) => {
    if (!abortedItemToEdit?._id) return;
    setAbortedEditSaving(true);
    try {
      await updateWashingCycle(abortedItemToEdit._id, data);
      toast({
        title: 'Éxito',
        description: 'Ciclo abortado actualizado correctamente.',
      });
      handleCloseAbortedEdit();
      // Refrescamos la tabla de abortados para que se vea el cambio.
      await fetchAndSetAbortedCycles();
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error?.response?.data?.message || error?.message || 'No se pudo actualizar el ciclo abortado.',
        variant: 'destructive',
      });
    } finally {
      setAbortedEditSaving(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setCurrentPage(1);
    setFilters({ ...newFilters, idLavanderia });
  };

  const handleRemoveFilter = (key) => {
    setCurrentPage(1);
    const newFilters = { ...filters };
    if (key !== 'idLavanderia') {
      delete newFilters[key];
      setFilters(newFilters);
    }
  };

  // Manejar cambios en los contadores de máquinas
  const handleCounterChange = (machineId, value) => {
    // Validar que el valor sea un número y no sea menor que el contador actual
    const numValue = parseFloat(value) || 0;

    setNewCounterValues(prev => ({
      ...prev,
      [machineId]: numValue
    }));
  };

  // Calcular la diferencia entre contadores (ciclos)
  const calculateCycles = (machineId) => {
    const currentValue = machineCounters[machineId] || 0;
    const newValue = newCounterValues[machineId] || 0;
    const difference = Math.max(0, newValue - currentValue);
    return difference;
  };

  /**
   * Obtiene la fecha por defecto (día anterior) en formato YYYY-MM-DD.
   * NOTA: el nombre `yesterday` sugiere restar un día, pero `setDate(getDate())`
   * es un no-op. Se conserva el comportamiento actual para no afectar el flujo
   * de contadores; corregir el día y la zona (usar hora local) es un fix aparte.
   */
  const getDefaultDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate());
    return yesterday.toISOString().split('T')[0];
  };

  /**
   * Maneja el cambio en el input de fecha por máquina
   */
  const handleDateChange = (machineId, value) => {
    setSelectedDates(prev => ({
      ...prev,
      [machineId]: value
    }));
  };

  /**
   * Inicia el modo de edición del contador para una máquina
   */
  const handleStartEditCounter = (machineId, currentCounter) => {
    setEditingCounter(prev => ({ ...prev, [machineId]: true }));
    setTempCounterValues(prev => ({ ...prev, [machineId]: currentCounter }));
  };

  /**
   * Cancela la edición del contador
   */
  const handleCancelEditCounter = (machineId) => {
    setEditingCounter(prev => ({ ...prev, [machineId]: false }));
    setTempCounterValues(prev => ({ ...prev, [machineId]: '' }));
  };

  /**
   * Guarda el nuevo valor del contador
   */
  const handleSaveCounter = async (machine) => {
    const newCounter = parseFloat(tempCounterValues[machine._id]);

    if (isNaN(newCounter) || newCounter < 0) {
      toast({
        title: "Error",
        description: "El contador debe ser un número válido mayor o igual a 0",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      await updateMachineCounter(machine._id, newCounter);

      toast({
        title: "Éxito",
        description: `Contador actualizado para ${machine.nombre}`,
      });

      // Limpiar estados de edición
      setEditingCounter(prev => ({ ...prev, [machine._id]: false }));
      setTempCounterValues(prev => ({ ...prev, [machine._id]: '' }));

      // Actualizar contadores locales
      setMachineCounters(prev => ({
        ...prev,
        [machine._id]: newCounter
      }));

      // Actualizar las máquinas para reflejar el nuevo contador
      setMachines(prev => prev.map(m => {
        if (m._id === machine._id) {
          return { ...m, contador: newCounter };
        }
        return m;
      }));

      // Actualizar newCounterValues para reflejar el cambio
      setNewCounterValues(prev => ({
        ...prev,
        [machine._id]: newCounter
      }));
    } catch (error) {
      console.error('Error al actualizar contador:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No se pudo actualizar el contador.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refresca AMBAS tablas (normal y abortados) cuando el modal
   * confirma un nuevo ciclo abortado. Lo conectamos vía
   * `onCycleCreated` del componente <AbortedCyclesModal />.
   */
  const refreshAfterAborted = useCallback(() => {
    fetchAndSetAbortedCycles();
    fetchAndSetWashingCycles();
  }, [fetchAndSetAbortedCycles, fetchAndSetWashingCycles]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="p-4 md:p-6 lg:p-8"
    >
      <div className="mb-4">
        <Button asChild variant="outline">
          <Link to={`/centros-lavado/${idLavanderia}`}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver al Centro de Lavado
          </Link>
        </Button>
      </div>

      <div className="mb-6">
        <div className="border border-border rounded-lg p-4 bg-card">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold tracking-tight">Registro de Ciclos de Lavado</h1>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAbortedModalOpen(true)}
              className="text-muted-foreground hover:text-foreground"
            >
              <Ban className="mr-2 h-4 w-4" />
              Registrar Ciclos Abortados
            </Button>
          </div>
          <p className="text-muted-foreground">
            Actualiza el contador de cada lavadora para el registro de los ciclos de lavado
          </p>
        </div>
      </div>

      {/* Sección de contadores de máquinas */}
      <div className="mb-6">
        {loadingMachines ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-sm text-muted-foreground">Cargando máquinas...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {machines.map(machine => (
              <Card
                key={machine._id}
                className="overflow-hidden card-gradient-bg border shadow-sm"
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex justify-between">
                    {machine.nombre}
                    <Badge variant="outline" className="ml-2">
                      {machine.marca}
                    </Badge>
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Capacidad: {machine.capacidad || 0} kg
                  </CardDescription>
                </CardHeader>

                <CardContent className="pb-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Contador actual</p>
                      {editingCounter[machine._id] ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={tempCounterValues[machine._id] || ''}
                            onChange={(e) => setTempCounterValues(prev => ({ ...prev, [machine._id]: e.target.value }))}
                            className="h-8 w-20"
                            min="0"
                            step="1"
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => handleSaveCounter(machine)}
                          >
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleCancelEditCounter(machine._id)}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <p className="font-medium">{machineCounters[machine._id] || 0}</p>
                          {isAdmin() && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => handleStartEditCounter(machine._id, machineCounters[machine._id] || 0)}
                              title="Editar contador"
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Nuevo contador</p>
                      <Input
                        type="number"
                        min={machineCounters[machine._id] || 0}
                        value={newCounterValues[machine._id] || ''}
                        onChange={(e) => handleCounterChange(machine._id, e.target.value)}
                        className="h-8"
                      />
                    </div>
                  </div>

                  {machine.consumoLimiteDia && (
                    <div className="flex items-center gap-2 px-2 py-1.5 mt-2 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-md">
                      <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-500" />
                      <span className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                        Límite diario: {machine.consumoLimiteDia} ciclos
                      </span>
                    </div>
                  )}

                  {/* Selector de fecha por máquina */}
                  <div className="mt-3">
                    <Label htmlFor={`date-${machine._id}`} className="text-xs">Fecha del registro</Label>
                    <Input
                      id={`date-${machine._id}`}
                      type="date"
                      value={selectedDates[machine._id] || getDefaultDate()}
                      onChange={(e) => handleDateChange(machine._id, e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      className="h-8 mt-1"
                    />
                  </div>

                  <div className="mt-3">
                    <p className="text-xs text-muted-foreground mb-1">Ciclos calculados</p>
                    {(() => {
                      const cycles = calculateCycles(machine._id);
                      const exceedsLimit = machine.consumoLimiteDia && cycles > machine.consumoLimiteDia;
                      const nearLimit = machine.consumoLimiteDia && cycles > machine.consumoLimiteDia * 0.8 && cycles <= machine.consumoLimiteDia;
                      
                      return (
                        <div className={`flex items-center p-2 rounded-md ${
                          exceedsLimit ? 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800' :
                          nearLimit ? 'bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800' :
                          cycles > 0 ? 'bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800' : ''
                        }`}>
                          <div className="flex-1">
                            <p className={`font-bold text-lg ${
                              exceedsLimit ? 'text-red-700 dark:text-red-400' :
                              nearLimit ? 'text-amber-700 dark:text-amber-400' :
                              'text-blue-700 dark:text-blue-400'
                            }`}>
                              {cycles}
                            </p>
                            {exceedsLimit && (
                              <p className="text-xs text-red-600 dark:text-red-400 font-medium mt-1">
                                ⚠ Supera el límite
                              </p>
                            )}
                           
                          </div>
                          {cycles > 0 && (
                            <Badge className={`ml-2 ${
                              exceedsLimit ? 'bg-red-100 text-red-800 hover:bg-red-100' :
                              nearLimit ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' :
                              'bg-green-100 text-green-800 hover:bg-green-100'
                            }`}>
                              {(cycles * machine.capacidad).toFixed(0)} kg
                            </Badge>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </CardContent>

                <CardFooter className="pt-2">
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={calculateCycles(machine._id) <= 0}
                    onClick={() => handleSaveItem({
                      maquina: { id: machine._id, nombre: machine.nombre },
                      ciclos: calculateCycles(machine._id),
                      capacidad: machine.capacidad || 0,
                      kilosLavados: (calculateCycles(machine._id) * machine.capacidad).toFixed(0),
                      lavanderia: { id: idLavanderia, nombre: laundryName },
                      contador: machine.contador || 0,
                      contadorNuevo: machine.contador + calculateCycles(machine._id),
                    })}
                  >
                    Guardar ciclos
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Card className="card-gradient-bg border shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl font-semibold flex items-center">
            <CircleSlash className="mr-2 h-5 w-5 text-primary" />
            Ciclos de Lavado
          </CardTitle>
          <CardDescription>
            Registra diariamente los ciclos de lavado completados por cada máquina
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="mb-4 flex flex-wrap gap-2 justify-between items-center">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsDrawerOpen(true)}
              >
                <FilterIcon className="mr-2 h-4 w-4" /> Filtros
              </Button>
              <ExportExcelButton
                service={async ({ query }) => {
                  const res = await fetchWashingCycles({ limit: 10000, page: 1, query });
                  return res?.data || [];
                }}
                filters={buildQuery(filters)}
                columns={excelColumns}
                fileName="ciclos-lavado"
              />
            </div>
            <div>
              <AppliedFilters
                filters={filters}
                fields={columns.filter(c => c.isFilter)}
                onRemoveFilter={handleRemoveFilter}
                ignoreKeys={['idLavanderia']}
              />
            </div>
          </div>

          <DataTable
            data={displayedData.map(item => ({
              ...item,
              // Asegurar que objetos complejos se rendericen correctamente
              maquina: typeof item.maquina === 'object' ? item.maquina : { nombre: 'Sin máquina' },
              lavanderia: typeof item.lavanderia === 'object' ? item.lavanderia : { nombre: 'Sin lavandería' },
            }))}
            columns={columns}
            isLoading={isLoading || loadingMachines}
            onAction={handleAction}
            showCreateButton={false}
          />

          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>

      {/* Tabla de CICLOS ABORTADOS.
          Se muestra SIEMPRE VISIBLE debajo de la tabla principal para
          que el operador no tenga que abrir un modal sólo para ver si
          quedó algún ciclo mal registrado. Visualmente diferenciada
          (borde y fondo ámbar) para evitar confundirla con los
          ciclos normales. */}
      <Card className="card-gradient-bg border border-amber-300/70 dark:border-amber-700/60 shadow-sm mt-6">
        <CardHeader className="pb-2 rounded-t-lg bg-amber-50/50 dark:bg-amber-950/10">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-xl font-semibold flex items-center text-amber-700 dark:text-amber-400">
                <Ban className="mr-2 h-5 w-5" />
                Ciclos de Lavado Abortados
              </CardTitle>
              <CardDescription className="mt-1">
                Histórico de ciclos que <strong>no se completaron</strong>. Se llevan
                aparte para no mezclarse con los ciclos normales y evitar confusiones
                con la fecha y la razón.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAbortedModalOpen(true)}
              className="border-amber-400 text-amber-700 hover:bg-amber-50 hover:text-amber-800 hover:border-amber-500 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-950/30"
            >
              <Ban className="mr-2 h-4 w-4" />
              Registrar ciclo abortado
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <div className="mb-4 flex flex-wrap gap-2 justify-between items-center">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setAbortedFilters((prev) => ({ ...prev }));
                  setAbortedCurrentPage(1);
                  fetchAndSetAbortedCycles();
                }}
                disabled={abortedLoading}
                title="Refrescar lista de abortados"
              >
                {abortedLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CircleSlash className="mr-2 h-4 w-4" />
                )}
                Refrescar
              </Button>
              <ExportExcelButton
                service={async ({ query }) => {
                  // Exporta desde el endpoint dedicado de abortados
                  // para mantener consistencia con la tabla.
                  const res = await fetchAbortedWashingCycles({ limit: 10000, page: 1, query });
                  return res?.data || [];
                }}
                filters={buildQuery(abortedFilters)}
                columns={columnsExcel}
                fileName="ciclos-lavado-abortados"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Total: <strong className="text-amber-700 dark:text-amber-400">{abortedTotalItems}</strong>{' '}
              ciclo{abortedTotalItems === 1 ? '' : 's'} abortado{abortedTotalItems === 1 ? '' : 's'}.
            </p>
          </div>

          {abortedData.length === 0 && !abortedLoading ? (
            <div className="flex flex-col items-center justify-center py-10 text-center text-sm text-muted-foreground border border-dashed border-amber-300/60 rounded-md">
              <Ban className="h-8 w-8 mb-2 text-amber-500" />
              <p className="font-medium text-foreground">No hay ciclos abortados registrados</p>
              <p className="text-xs mt-1 max-w-md">
                Cuando un ciclo no se complete, usa el botón <em>Registrar ciclo abortado</em>
                {' '}para que quede trazable por separado.
              </p>
            </div>
          ) : (
            <DataTable
              data={abortedData.map((item) => ({
                ...item,
                maquina: typeof item.maquina === 'object' ? item.maquina : { nombre: 'Sin máquina' },
                lavanderia: typeof item.lavanderia === 'object' ? item.lavanderia : { nombre: 'Sin lavandería' },
              }))}
              columns={abortedColumns}
              isLoading={abortedLoading}
              onAction={handleAction}
              showCreateButton={false}
            />
          )}

          <Pagination
            currentPage={abortedCurrentPage}
            totalItems={abortedTotalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setAbortedCurrentPage}
          />
        </CardContent>
      </Card>

      <AnimatePresence>
        {isModalOpen && modalMode === 'edit' && (
          <EditWashingCycleModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onSave={handleSaveItem}
            item={currentItem}
            machines={machines}
            isLoading={isLoading}
          />
        )}
        {isModalOpen && modalMode === 'create' && (
          <DynamicFormModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onSave={handleSaveItem}
            item={currentItem}
            fields={columns.filter(c => c.isForm !== false)}
            title="Registrar Ciclos de Lavado"
            mode={modalMode}
          />
        )}
      </AnimatePresence>

      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        fields={columns.filter(c => c.isFilter)}
        initialFilters={filters}
        onChange={handleFilterChange}
        onApply={handleFilterChange}
        initialDay={true}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro de eliminar este registro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente el registro de ciclos
              de la lavadora {itemToDelete?.maquina?.nombre || ''}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setItemToDelete(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItem} className="bg-destructive hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de ciclos abortados.
          Usa el componente compartido con la página de recepción. Al
          confirmar un registro, refresca tanto la tabla de ciclos
          normales (por si quedó mezclado) como la tabla inferior de
          abortados. */}
      <AbortedCyclesModal
        isOpen={abortedModalOpen}
        onClose={() => setAbortedModalOpen(false)}
        machines={machines}
        dryers={[]}
        idLavanderia={idLavanderia}
        laundryName={laundryName}
        onCycleCreated={refreshAfterAborted}
      />

      {/* Modal de EDICIÓN de un ciclo abortado.
          Componente específico (NO reutiliza EditWashingCycleModal)
          porque la edición de un abortado es semánticamente distinta:
          no se tocan contadores ni kilos lavados, sólo fecha,
          lavadora, ciclos y razón. */}
      <EditAbortedWashingCycleModal
        isOpen={abortedEditModalOpen}
        onClose={handleCloseAbortedEdit}
        onSave={handleSaveAbortedEdit}
        item={abortedItemToEdit}
        machines={machines}
        isLoading={abortedEditSaving}
      />
    </motion.div>
  );
};

export default WashingCyclesPage;
